'use client'

import { ComponentPropsWithoutRef, ReactNode, useCallback, useEffect, useRef } from 'react'

interface ListKeyboardNavProps extends ComponentPropsWithoutRef<'div'> {
    children: ReactNode
    onOtherKeyDown?: (e: KeyboardEvent) => void
}

/**
 * A component that provides keyboard navigation for a list of children.
 * Supports arrow key navigation (up/down) and Enter key activation.
 *
 * @example
 * Basic usage:
 * ```tsx
 * <ListKeyboardNav>
 *   <button>Item 1</button>
 *   <button>Item 2</button>
 *   <button>Item 3</button>
 * </ListKeyboardNav>
 * ```
 *
 * @example
 * With input (search/autocomplete pattern):
 * ```tsx
 * <div>
 *   <input type="text" placeholder="Search..." />
 *   <ListKeyboardNav>
 *     <button onClick={() => selectItem(1)}>Result 1</button>
 *     <button onClick={() => selectItem(2)}>Result 2</button>
 *     <button onClick={() => selectItem(3)}>Result 3</button>
 *   </ListKeyboardNav>
 * </div>
 * // When focused on input, press Enter or ArrowDown to jump to first result
 * // When at end/beginning of list, focus returns to the initially focused element
 * ```
 */
const ListKeyboardNav = ({ children, onOtherKeyDown, ...rest }: ListKeyboardNavProps) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const initialFocusedElementRef = useRef<HTMLElement | null>(null)

    const returnToInitialFocus = useCallback(() => {
        initialFocusedElementRef.current?.focus()
    }, [])

    const getActiveIndex = useCallback((): number => {
        if (!containerRef.current) return -1

        return Array.from(containerRef.current.children).findIndex(
            (element) => element === document.activeElement,
        )
    }, [])

    /**
     * Finds the next navigable child starting from `start` and moving by `step`, skipping
     * non-focusable nodes (e.g. group headings). Returns null if none.
     *
     * "Navigable" means focusable: a natively focusable element or one carrying a `tabindex`
     * (options set `tabIndex={-1}` for roving focus). Decorative children such as group
     * headings are plain, non-focusable elements and are skipped without needing any marker
     * attribute, keeping navigation decoupled from accessibility concerns.
     *
     * Iterates over `children` (an HTMLCollection of elements only) rather than `childNodes`,
     * so raw text nodes can never be returned and `.focus()`/`.click()` are always safe to call.
     */
    const findNavigableChild = useCallback((start: number, step: number): HTMLElement | null => {
        const children = containerRef.current?.children
        if (!children) return null

        const isNavigable = (element: HTMLElement): boolean =>
            element.matches('button, a[href], input, select, textarea, [tabindex]') &&
            !element.hasAttribute('disabled')

        for (let i = start; i >= 0 && i < children.length; i += step) {
            const child = children[i] as HTMLElement
            if (child && isNavigable(child)) {
                return child
            }
        }
        return null
    }, [])

    const down = useCallback(() => {
        if (!containerRef.current) return

        const index = getActiveIndex()

        if (index === -1) {
            // If no item is currently focused, focus on the first navigable item
            findNavigableChild(0, 1)?.focus()
        } else {
            // Focus the next navigable item, or return focus if there is none
            const nextChild = findNavigableChild(index + 1, 1)
            if (nextChild) {
                nextChild.focus()
            } else {
                returnToInitialFocus()
            }
        }
    }, [getActiveIndex, findNavigableChild])

    const up = useCallback(() => {
        if (!containerRef.current) return

        const index = getActiveIndex()
        const prevChild = index > 0 ? findNavigableChild(index - 1, -1) : null
        if (prevChild) {
            prevChild.focus()
        } else {
            // If at the first item, return focus to the element that was focused before the component mounted
            returnToInitialFocus()
        }
    }, [getActiveIndex, findNavigableChild])

    const onKeyDown = useCallback(
        (e: KeyboardEvent) => {
            const activeElement = document.activeElement as HTMLElement

            // Only handle if focus is on the initial element (e.g., input) or within our container
            const isOnInitialElement = activeElement === initialFocusedElementRef.current
            const isWithinContainer = containerRef.current?.contains(activeElement)

            if (!isOnInitialElement && !isWithinContainer) {
                return
            }

            if (e.key === 'ArrowDown') {
                e.preventDefault()
                down()
            } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                up()
            } else if (e.key === 'Enter') {
                e.preventDefault()
                if (activeElement?.tagName === 'INPUT') {
                    // If the focused element is an INPUT (like a search box)
                    // click the first navigable child (skipping headings) instead of the search box itself
                    findNavigableChild(0, 1)?.click()
                } else {
                    activeElement?.click()
                }
            } else if (e.key === 'Escape') {
                e.preventDefault()
                returnToInitialFocus()
            } else if (onOtherKeyDown) {
                onOtherKeyDown(e)
            }
        },
        [down, up, returnToInitialFocus, findNavigableChild],
    )

    useEffect(() => {
        // Store the initially focused element when component mounts
        initialFocusedElementRef.current = document.activeElement as HTMLElement

        window.addEventListener('keydown', onKeyDown)

        return () => {
            window.removeEventListener('keydown', onKeyDown)
        }
    }, [onKeyDown])

    return (
        <div ref={containerRef} {...rest}>
            {children}
        </div>
    )
}

export default ListKeyboardNav
